import { Action, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Folder, FolderResponse } from '../../Types/folder';
import { getFolder, getFolderFromProjectId, getFolders } from '../actions/folderActions';
import { FOLDER_LIST_SIZE, myFolder } from '../consts';
import { RootState } from '../store';

interface FoldersState {
  unpinnedFolders: Folder[];
  pinnedFolders: Folder[];
  currentFolder?: Folder;
  currentFolderRequestId?: string;
  totalCount: number;
  isLoading: boolean;
  isScrolling: boolean;
  searchQuery: string;
  page: number;
  folderSearchSelected: boolean;
}

const initialState: FoldersState = {
  unpinnedFolders: [],
  pinnedFolders: [],
  currentFolder: myFolder,
  currentFolderRequestId: undefined,
  totalCount: 0,
  isLoading: false,
  isScrolling: false,
  searchQuery: '',
  page: 1,
  folderSearchSelected: false,
};

// Define the type for the rejected action
interface RejectedAction extends Action {
  payload: string;
}

const foldersSlice = createSlice({
  name: 'folders',
  initialState,
  reducers: {
    setCurrentFolder: (state, action: PayloadAction<Folder | undefined>) => {
      state.currentFolder = action.payload;
      state.currentFolderRequestId = undefined;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsScrolling: (state, action: PayloadAction<boolean>) => {
      state.isScrolling = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setFolderSearchSelected: (state, action: PayloadAction<boolean>) => {
      state.folderSearchSelected = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFolders.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getFolders.fulfilled, (state, action: PayloadAction<FolderResponse>) => {
        const { unpinnedFolders, pinnedFolders, totalCount } = action.payload;
        const pageOffset = FOLDER_LIST_SIZE * (state.page - 1);
        if (pageOffset === 0) {
          state.unpinnedFolders = unpinnedFolders;
          state.pinnedFolders = pinnedFolders;
        } else {
          state.unpinnedFolders = [...state.unpinnedFolders, ...unpinnedFolders];
          state.pinnedFolders = [...state.pinnedFolders, ...pinnedFolders];
        }
        state.totalCount = totalCount;
        state.isLoading = false;
      })
      .addCase(getFolder.pending, (state, action) => {
        state.currentFolderRequestId = action.meta.requestId;
      })
      .addCase(getFolder.fulfilled, (state, action) => {
        if (state.currentFolderRequestId !== action.meta.requestId) {
          return;
        }

        state.currentFolder = action.payload;
        state.currentFolderRequestId = undefined;
      })
      .addCase(getFolderFromProjectId.fulfilled, (state, action: PayloadAction<Folder>) => {
        state.currentFolder = action.payload;
        state.currentFolderRequestId = undefined;
      })
      .addMatcher(
        (action: Action): action is RejectedAction =>
          action.type.startsWith('folders/') && action.type.endsWith('/rejected'),
        (state) => {
          state.isLoading = false;
        }
      );
  },
});

export const {
  setCurrentFolder,
  setIsLoading,
  setIsScrolling,
  setPage,
  setSearchQuery,
  setFolderSearchSelected,
} = foldersSlice.actions;

export const selectFolders = (state: RootState) => state.folders;

export default foldersSlice.reducer;
